import { FaArrowUp } from 'react-icons/fa';
import { Table } from 'react-bootstrap';
import { useStaffTypes } from '../hooks/useStaffTypes';
import StaffTypeAbbr from './StaffTypeAbbr';
import './LabourDifferenceSummary.css';

const EMPTY_HOURS = {
    sn_hours_day: 0,
    sn_hours_night: 0,
    ena_hours_day: 0,
    ena_hours_night: 0,
    cw_hours_day: 0,
    cw_hours_night: 0,
};

export function emptyHourTotals() {
    return { ...EMPTY_HOURS };
}

/** Plan hours − quote hours (both in hours). Positive = plan over. */
export function labourDiffForRole(planHours, quoteHours, role) {
    const plan = (Number(planHours?.[`${role}_hours_day`]) || 0) + (Number(planHours?.[`${role}_hours_night`]) || 0);
    const quote = (Number(quoteHours?.[`${role}_hours_day`]) || 0) + (Number(quoteHours?.[`${role}_hours_night`]) || 0);
    return plan - quote;
}

function formatDiff(value) {
    const n = Number(value) || 0;
    return n.toFixed(2);
}

function DiffCell({ value }) {
    const n = Number(value) || 0;
    const planOver = n > 0.005;
    const quoteOver = n < -0.005;
    const abs = Math.abs(n);

    let hint = 'Service plan and quotation hours match';
    if (planOver) hint = `Service plan is ${formatDiff(abs)} hours over the quotation`;
    if (quoteOver) hint = `Quotation is ${formatDiff(abs)} hours over the service plan`;

    return (
        <div className="labour-diff-cell" title={hint}>
            <span className={`labour-diff-side labour-diff-side--plan${planOver ? ' is-over' : ''}`}>
                {planOver ? (
                    <>
                        <FaArrowUp aria-hidden />
                        <span className="labour-diff-side-label">Plan over</span>
                    </>
                ) : (
                    <span className="labour-diff-side-label">Plan</span>
                )}
            </span>
            <span
                className={`labour-diff-value${planOver ? ' is-plan-over' : ''}${quoteOver ? ' is-quote-over' : ''}${!planOver && !quoteOver ? ' is-balanced' : ''}`}
            >
                {formatDiff(n)}
            </span>
            <span className={`labour-diff-side labour-diff-side--quote${quoteOver ? ' is-over' : ''}`}>
                {quoteOver ? (
                    <>
                        <span className="labour-diff-side-label">Quote over</span>
                        <FaArrowUp aria-hidden />
                    </>
                ) : (
                    <span className="labour-diff-side-label">Quote</span>
                )}
            </span>
        </div>
    );
}

/**
 * Labour difference table: plan hours − quote hours, with over/under markers.
 * @param {{ planHours: object, quoteHours: object, compact?: boolean, title?: string }} props
 */
function LabourDifferenceSummary({
    planHours = EMPTY_HOURS,
    quoteHours = EMPTY_HOURS,
    compact = false,
    title = 'Service Plan Labour Difference (hours)',
}) {
    const { getAbbreviation, getDisplayName } = useStaffTypes();
    const sn = labourDiffForRole(planHours, quoteHours, 'sn');
    const ena = labourDiffForRole(planHours, quoteHours, 'ena');
    const cw = labourDiffForRole(planHours, quoteHours, 'cw');

    return (
        <Table striped bordered hover size={compact ? 'sm' : undefined} className={`labour-diff-table${compact ? ' labour-diff-table--compact' : ''}`}>
            <thead>
                <tr>
                    <th colSpan="4" style={{ textAlign: 'center' }}>
                        <strong>{title}</strong>
                    </th>
                </tr>
                <tr>
                    <th></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('SN')} displayName={getDisplayName('SN')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('ENA')} displayName={getDisplayName('ENA')} suffix="Hours" /></th>
                    <th style={{ textAlign: 'center' }}><StaffTypeAbbr abbreviation={getAbbreviation('CW')} displayName={getDisplayName('CW')} suffix="Hours" /></th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>Total Labour Hours Difference</td>
                    <td><DiffCell value={sn} /></td>
                    <td><DiffCell value={ena} /></td>
                    <td><DiffCell value={cw} /></td>
                </tr>
            </tbody>
        </Table>
    );
}

export default LabourDifferenceSummary;
